import { useQuery, useMutation } from '@tanstack/react-query';
import { queryClient } from '../../main';
import { toast } from 'react-toastify';
import { 
    fetchSepaClients, 
    fetchSepaTransactions, 
    toggleInactiveMonth, 
    closeSepaMonth, 
    deleteSepaClient 
} from '../api/sepaService';
import { Client } from '../../types/index';
import { getNextMonthAndYear } from '../../utils/utils';
import { useTenantCode } from './useTenantCode';

// =====================================================
// QUERY HOOKS
// =====================================================

/**
 * Hook para obtener clientes SEPA
 */
export const useSepaClientsQuery = () => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['sepa-clients', tenantCode],
        queryFn: fetchSepaClients,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
};

/**
 * Hook para obtener transacciones SEPA
 */
export const useSepaTransactionsQuery = () => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['sepa-transactions', tenantCode],
        queryFn: fetchSepaTransactions,
        staleTime: 2 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
};

// =====================================================
// MUTATION HOOKS
// =====================================================

/**
 * Hook para toggle de meses inactivos
 */
export const useToggleInactiveMonthMutation = () => useMutation({
    mutationFn: toggleInactiveMonth,
    onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['sepa-clients'] });
        toast.success('Mes inactivo actualizado correctamente');
    },
    onError: (error: any) => {
        console.error('Error updating inactive months:', error);
        toast.error('Error al actualizar mes inactivo');
    }
});

/**
 * Hook para cerrar mes SEPA
 */
export const useCloseSepaMonthMutation = () => useMutation({
    mutationFn: closeSepaMonth,
    onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ['sepa-clients'] });
        queryClient.invalidateQueries({ queryKey: ['sepa-transactions'] });
        toast.success('Mes cerrado exitosamente');
        return data;
    },
    onError: (error: any) => {
        console.error('Error closing the month:', error);
        
        if (error.response?.data?.message === "The month is already closed") {
            toast.info("El mes ya está cerrado.");
        } else {
            toast.error("Error al cerrar el mes: " + (error.response?.data?.message || error.message));
        }
    }
});

/**
 * Hook para eliminar cliente/suscripción SEPA
 */
export const useDeleteSepaClientMutation = () => useMutation({
    mutationFn: deleteSepaClient,
    onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ['sepa-clients'] });
        toast.success('Cliente dado de baja correctamente');
        return data;
    },
    onError: (error: any) => {
        console.error('Error deleting client and subscription:', error);
        toast.error('Error dando de baja cliente: ' + (error.response?.data?.message || error.message), { 
            autoClose: false 
        });
    }
});

// =====================================================
// UTILITY FUNCTIONS
// =====================================================

/**
 * Función para formatear números con separadores de miles
 */
export const formatSepaNumber = (num: number): string => {
    const parts = num.toFixed(2).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return parts.join(',');
};

/**
 * Función para obtener los próximos 12 meses
 */
export const getNext12Months = () => {
    const months = [
        { name: "Ene", index: 1 },
        { name: "Feb", index: 2 },
        { name: "Mar", index: 3 },
        { name: "Abr", index: 4 },
        { name: "May", index: 5 },
        { name: "Jun", index: 6 },
        { name: "Jul", index: 7 },
        { name: "Ago", index: 8 },
        { name: "Sep", index: 9 },
        { name: "Oct", index: 10 },
        { name: "Nov", index: 11 },
        { name: "Dic", index: 12 }
    ];
    const currentMonth = new Date().getMonth();
    const next12Months = [];
    for (let i = 1; i <= 12; i++) {
        next12Months.push(months[(currentMonth + i) % 12]);
    }
    return next12Months;
};

/**
 * Función para obtener el nombre completo del próximo mes
 */
export const getNextFullMonth = () => {
    const mesesCompletos = [
        { shortName: 'Ene', longName: 'Enero' },
        { shortName: 'Feb', longName: 'Febrero' },
        { shortName: 'Mar', longName: 'Marzo' },
        { shortName: 'Abr', longName: 'Abril' },
        { shortName: 'May', longName: 'Mayo' },
        { shortName: 'Jun', longName: 'Junio' },
        { shortName: 'Jul', longName: 'Julio' },
        { shortName: 'Ago', longName: 'Agosto' },
        { shortName: 'Sep', longName: 'Septiembre' },
        { shortName: 'Oct', longName: 'Octubre' },
        { shortName: 'Nov', longName: 'Noviembre' },
        { shortName: 'Dic', longName: 'Diciembre' },
    ];

    const nextMonth = getNext12Months()[0].name;
    
    for (const mes of mesesCompletos) {
        if (mes.shortName === nextMonth) {
            return mes.longName;
        }
    }
    return 'Mes no encontrado';
};

/**
 * Calcular el total de importes de suscripciones menos descuentos
 */
export const calculateTotalAmount = (clients: Client[]): string => {
    let total = 0;
    clients.forEach(client => {
        client.suscripciones.forEach(suscripcion => {
            total += suscripcion.importeSuscripcion - (suscripcion.descuento || 0);
        });
    });
    return formatSepaNumber(total);
};

/**
 * Función auxiliar para actualizar observaciones (limpiarlas después de cerrar mes)
 */
export const updateClientObservations = (clients: Client[]): Client[] => {
    return clients.map(client => ({
        ...client,
        suscripciones: client.suscripciones.map(suscripcion => ({
            ...suscripcion,
            observaciones: ""
        }))
    }));
};